import { VariantProps } from 'class-variance-authority';
import {
  ComponentPropsWithoutRef,
  ComponentPropsWithRef,
  ElementType,
  JSXElementConstructor,
} from 'react';
import { Simplify } from 'type-fest';

export type PropsOf<C extends keyof JSX.IntrinsicElements | JSXElementConstructor<unknown>> =
  JSX.LibraryManagedAttributes<C, ComponentPropsWithoutRef<C>>;

type AsProp<C extends ElementType> = {
  /**
   * An override of the default HTML tag.
   * Can also be another React component.
   */
  as?: C;
};

/**
 * Allows for extending a set of props (`ExtendedProps`) by an overriding set of props
 * (`OverrideProps`), ensuring that any duplicates are overridden by the overriding
 * set of props.
 */
export type ExtendableProps<ExtendedProps = {}, OverrideProps = {}> = OverrideProps &
  Omit<ExtendedProps, keyof OverrideProps>;

/**
 * Allows for inheriting the props from the specified element type so that
 * props like children, className & style work, as well as element-specific
 * attributes like aria roles. The component (`C`) must be passed in.
 */
export type InheritableElementProps<C extends ElementType, Props = {}> = ExtendableProps<
  PropsOf<C>,
  Props
>;

/**
 * A more sophisticated version of `InheritableElementProps` where
 * the passed in `as` prop will determine which props can be included
 */
export type PolymorphicComponentProps<C extends ElementType, Props = {}> = InheritableElementProps<
  C,
  Props & AsProp<C>
>;

export type PolymorphicRef<C extends ElementType> = ComponentPropsWithRef<C>['ref'];

export type PolymorphicComponentPropsWithRef<
  C extends ElementType,
  Props = {},
> = PolymorphicComponentProps<C, Props> & { ref?: PolymorphicRef<C> };

export enum ESelectIntents {
  filled = 'filled',
  default = 'default',
  light = 'light',
}

export enum EButtonIntents {
  Filled = 'filled',
  LightFilled = 'light-filled',
  Linear = 'linear',
  LightLinear = 'light-linear',
  Text = 'text',
}

export enum EInputIntents {
  filled = 'filled',
  outlined = 'outlined',
}

export enum ETagIntents {
  filled = 'filled',
  linear = 'linear',
  light = 'light',
}

export enum ETagToneIntents {
  Natural = 'natural',
  Positive = 'positive',
  Negative = 'negative',
  Warning = 'warning',
  Primary = 'primary',
}

export enum ETypographyIntents {
  H1 = 'H1',
  H1Phone = 'H1Phone',
  H2 = 'H2',
  H2Phone = 'H2Phone',
  H3 = 'H3',
  Action = 'Action',
  Regular = 'Regular',
  Badge = 'Badge',
  Label = 'Label',
}

export enum ETimePickerFormat {
  Minute = 'HH:mm',
  Second = 'HH:mm:ss',
}

export enum ETooltipIntents {
  Info = 'info',
  Question = 'question',
}

export enum ENumberDisplayIntents {
  Normal = 'normal',
  Primary = 'primary',
  Alarmed = 'alarmed',
}

export type RequiredVariantProps<
  T extends (...args: any) => any,
  Keys extends keyof VariantProps<T> = keyof VariantProps<T>,
> = Simplify<
  VariantProps<T> & {
    [Variant in Keys]: Exclude<VariantProps<T>[Variant], null | undefined>;
  }
>;

export type SVGComponent = React.ComponentType<React.SVGAttributes<SVGSVGElement>>;
